import { useEffect, useRef, useState } from "react";
import { ImageOff } from "lucide-react";

import { HGRecordMedia } from "@/common/types";
import { computeMosaic } from "@/common/mosaic";

// Track the live pixel width of an element so the mosaic can reflow on resize.
function useMeasuredWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

export function PhotoAlbum({
  images,
  onOpen,
}: {
  images: HGRecordMedia[];
  onOpen: (index: number) => void;
}) {
  const [ref, width] = useMeasuredWidth<HTMLDivElement>();
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState<Set<string>>(new Set());

  const layout =
    width > 0
      ? computeMosaic(images, {
          maxWidth: width,
          maxHeight: Math.min(width * 1.5, window.innerHeight * 0.7),
          spacing: 2,
        })
      : null;

  return (
    <div ref={ref} className="w-full">
      {layout && (
        <div
          className="relative overflow-hidden rounded-xl"
          style={{ width: layout.width, height: layout.height }}
        >
          {layout.tiles.map((tile) => {
            const style = {
              left: tile.left,
              top: tile.top,
              width: tile.width,
              height: tile.height,
            } as const;
            if (failed.has(tile.media.id)) {
              return (
                <div
                  key={tile.media.id}
                  className="absolute flex items-center justify-center bg-muted"
                  style={style}
                >
                  <ImageOff className="h-5 w-5 text-muted-foreground" />
                </div>
              );
            }
            const isLoaded = loaded.has(tile.media.id);
            return (
              <div
                key={tile.media.id}
                className={`absolute overflow-hidden bg-muted ${
                  isLoaded ? "" : "animate-pulse"
                }`}
                style={style}
              >
                <img
                  src={tile.media.thumbnail_url || tile.media.url}
                  alt={tile.media.caption || tile.media.original_filename}
                  loading="lazy"
                  draggable={false}
                  onClick={() => onOpen(images.indexOf(tile.media))}
                  onLoad={() =>
                    setLoaded((prev) =>
                      prev.has(tile.media.id)
                        ? prev
                        : new Set(prev).add(tile.media.id),
                    )
                  }
                  // Presigned URLs expire — keep the tile but show a placeholder
                  onError={() =>
                    setFailed((prev) => new Set(prev).add(tile.media.id))
                  }
                  className={`h-full w-full cursor-pointer object-cover transition-opacity duration-200 hover:opacity-95 ${
                    isLoaded ? "opacity-100" : "opacity-0"
                  }`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
